import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { Form, message, Modal, Row } from 'antd';
import styled from 'styled-components';
import moment from 'utils/moment';
import type { FormValues, ScheduleTimeOffModalProps } from './services/types';
import type { IBodyAddBlockHours } from 'services/types/blockHours';
import apis from 'services/apis';
import { REASON_TAGS } from './constants';
import shopSelectors from 'services/selectors/shop';
import StaffList from './components/StaffList';
import FormSectionPanel from './components/FormSection';
import HistoryActivities from './components/HistoryActivities';
import { loadingPageSignal } from 'components/LoadingPage';
import { Moment } from 'moment';
import Text from 'components/Text';
import { useAppDispatch } from 'store/hooks';
import shopActions from 'services/actions/shop';
import { useWatch } from 'antd/es/form/Form';
import get from 'lodash/get';
import { toast } from 'react-toastify';

export interface ScheduleTimeOffModalRef {
  open: () => void;
  close: () => void;
}

export const scheduleModalRef = React.createRef<ScheduleTimeOffModalRef>();
const ScheduleTimeOffModal = forwardRef<ScheduleTimeOffModalRef, ScheduleTimeOffModalProps>(
  ({  }, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const [form] = Form.useForm<FormValues>();
    const staffs = shopSelectors.staffsAvailable() || [];
    const dispatch = useAppDispatch();
    const id = useWatch('id', form);
    const scheduleKey = moment().format?.('ddd') || 'Mon';
    const schedule = shopSelectors.scheduleBooking()?.[scheduleKey] || null;
    const freshStaffs = shopSelectors.staffsAvailable() || [];
    const freshSchedule = shopSelectors.scheduleBooking()?.[moment().format?.('ddd') || scheduleKey] || null;

    const handleClose = () => setIsOpen(false);

    useImperativeHandle(ref, () => ({
      open: () => {
        const selectedStaffId = form.getFieldValue('staffId') || freshStaffs[0]?.id;

        if (selectedStaffId) {
          dispatch(shopActions.blockHourHistories.fetch({
            staffId: String(selectedStaffId),
            page: 1,
            pageSize: 20,
          }));
        }

        form.setFieldsValue({
          staffId: selectedStaffId || '',
          selectedTimeStart: freshSchedule ? moment(freshSchedule.startTime, 'HH:mm').format('hh:mm A') : (form.getFieldValue('selectedTimeStart') || '09:00AM'),
          selectedTimeEnd: freshSchedule ? moment(freshSchedule.endTime, 'HH:mm').format('hh:mm A') : (form.getFieldValue('selectedTimeEnd') || '08:00PM'),
        });

        setIsOpen(true);
      },
      close: handleClose,
    }), /* no deps here because functions read fresh state when called */);
    
    const buildDateTime = (date: Moment, time: string) => {    
      const normalizedTime = time.replace(/([AP]M)$/i, ' $1');
      return moment(`${date.format('MM-DD-YYYY')} ${normalizedTime}`, 'MM-DD-YYYY hh:mm A').format('MM-DD-YYYY HH:mm:ss');
    };

    const handleOk = async (): Promise<void> => {
      loadingPageSignal.value = true;
      try {
        const values = await form.validateFields();
        
        const payload: IBodyAddBlockHours = {
          id: values.id ? Number(values.id) : undefined,
          staffId: Number(values.staffId),
          reason: values.reason,
          startTime: buildDateTime(values.date, values.selectedTimeStart),
          endTime: buildDateTime(values.date, values.selectedTimeEnd),
        };

        const res = await apis.addBlockHoursV2(payload);
        if(res?.data?.payload) {
          setIsOpen(false);
          handleClose();
          dispatch(shopActions.blockHourHistories.fetch({
            staffId: String(values.staffId),
            page: 1,
            pageSize: 20,
          }));
        }
      } catch (error) {
        message.error('Failed to schedule time off');
      } finally {
        loadingPageSignal.value = false;
      }
    };


    return (
      <ModalStyled
        open={isOpen}
        onCancel={handleClose}
        closable={false}
        onOk={handleOk}
        okText={id ? 'UPDATE' : 'SCHEDULE'}
        cancelText="CLOSE"
        width={1200}
        styles={{ body: { padding: 0 } }}
        okButtonProps={{ style: { background: '#1a1a1a' } }}
      >
        <Form<FormValues>
          form={form}
          layout="vertical"
          initialValues={{
            date: moment(),
            reason: '',
            staffId: staffs[0]?.id || '',
            selectedTimeStart: '09:00AM',
            selectedTimeEnd: '08:00PM',
          }}
        >
          <Header justify={'space-between'}>
            <Text className="title">{'SCHEDULE TIME OFF'}</Text>
          </Header>
          <BodyWrapper>
            <Form.Item name={'selectedTimeEnd'}/>
            <Form.Item name={'selectedTimeStart'}/>
            <Form.Item name={'id'}/>
            <Form.Item name='staffId' noStyle>
              <StaffList />
            </Form.Item>
            <FormSectionPanel
              reasonTags={REASON_TAGS}
            />
            <HistoryActivities />
          </BodyWrapper>
        </Form>
      </ModalStyled>
    );
  },
);

ScheduleTimeOffModal.displayName = 'ScheduleTimeOffModal';

export default ScheduleTimeOffModal;

const Header = styled(Row)`
  height: 50px;
  position: relative;
  align-items: center;
  border-top-right-radius: 5px;
  border-top-left-radius: 5px;
  border-bottom: 1px solid #148CF1;
  background: #E8F3FF;

  .title {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    color: #1D2129;
    text-align: center;
    font-family: Poppins;
    font-size: 20px;
    font-style: normal;
    font-weight: 600;
    line-height: 24px; /* 120% */
  }
  button {
    background: #fff;
    align-self: stretch;
    border-radius: 5px;
    width: 80px;
    
    &:hover {
      background: #FFEFEB;
    }

    &:disabled {
      opacity: 0.5;
    }
  }
  .text-action {
    color: #148CF1;
    font-family: Poppins;
    font-size: 22px;
    font-style: normal;
    font-weight: 600;
    line-height: 24px; /* 133.333% */
    white-space: nowrap;
  }
`;

const ModalStyled = styled(Modal)`
  .ant-modal-content { padding: 0; border-radius: 12px; overflow: hidden; }
  .ant-modal-header {
    margin: 0; padding: 16px 24px;
    border-bottom: 1px solid #e8e8e8;
    text-align: center; background: #fff;
    .ant-modal-title {
      font-family: 'Poppins', sans-serif; font-size: 18px;
      font-weight: 700; color: #1a1a1a; letter-spacing: 1px;
    }
  }
  .ant-modal-footer {
    margin: 0; padding: 12px 24px; border-top: 1px solid #e8e8e8;
    display: flex; gap: 12px;
    .ant-btn {
      flex: 1; height: 44px; font-family: 'Poppins', sans-serif;
      font-weight: 600; font-size: 15px; border-radius: 8px;
    }
    .ant-btn-default { border: 1.5px solid #d9d9d9; color: #555; }
    .ant-btn-primary { background: #1a1a1a; border: none; &:hover { background: #333; } }
  }
`;

const BodyWrapper = styled.div`
  display: flex;
  height: 550px;
  font-family: 'Poppins', sans-serif;
`;